// Alternation-based superset detection. Pure function — no I/O, no
// DDB calls. Given a session's set stream (plus optional explicit
// superset:<id> tags), emits a list of superset/circuit groups.
//
// The signal is alternation, not time. Antenore's wrist curl/extension
// session has 565 ms intra-pair *and* 2-10 min inter-round; a heavy
// bench/row pair can have minutes inside the alternation. Rest length
// is a confounder. So the detector ignores `performedAt` deltas
// entirely beyond using them to sort the input.
//
// Decision flow per set in the input:
//   1. If the set carries a `superset:<id>` tag → goes into that
//      explicit group regardless of position. Explicit beats derived.
//   2. Otherwise it's a candidate for derived detection. The detector
//      walks the candidates left-to-right and finds maximal contiguous
//      runs where every adjacent pair has different exerciseSlug. A
//      run of length ≥ 3 (i.e. ≥ 2 alternations, ≥ 2 distinct slugs)
//      becomes a derived group.
//
// Edge cases handled:
//   - Warmup A's followed by working A-B-A-B → first A's break the
//     alternation; the maximal alternating run starts later. The
//     warmups are not included in the group.
//   - Solo accessory after a superset: A-B-A-B-C → the run A-B-A-B
//     groups the superset; the trailing C is left ungrouped.
//   - Tri-set: A-B-C-A-B-C → 6-set alternating run, group includes
//     all three slugs.
//   - Single A-B (2 sets, 1 alternation): not enough; no group emitted.
//     The user can still tag it explicitly if they want it counted.
//   - Set with sessionless or missing performedAt: caller is responsible
//     for filtering — the detector trusts its inputs.

export type SetForSupersetDetect = {
  id: string;
  exerciseSlug: string;
  performedAt: string;
  tags?: readonly string[];
};

export type SupersetGroup = {
  id: string;
  source: "explicit" | "derived";
  // Distinct slugs in first-appearance order. For a superset of bench
  // and row, this is ["bench-press", "barbell-row"]; the UI labels the
  // group with these in order.
  slugs: string[];
  // Member set ids in performedAt order.
  setIds: string[];
};

const SUPERSET_TAG_PREFIX = "superset:";

const extractSupersetId = (tags: readonly string[] | undefined): string | null => {
  if (!tags) return null;
  for (const t of tags) {
    if (t.startsWith(SUPERSET_TAG_PREFIX)) {
      const id = t.slice(SUPERSET_TAG_PREFIX.length);
      if (id.length > 0) return id;
    }
  }
  return null;
};

const distinctInOrder = (xs: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) {
    if (!seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  }
  return out;
};

export function deriveSupersetGroups(
  sets: readonly SetForSupersetDetect[],
): SupersetGroup[] {
  if (sets.length < 2) return [];

  const sorted = [...sets].sort((a, b) =>
    a.performedAt < b.performedAt ? -1 : a.performedAt > b.performedAt ? 1 : 0,
  );

  // Pass 1: explicit groups via superset:<id> tag. Membership is by
  // tag, not by adjacency — a user can stamp the same id on two
  // non-adjacent sets to assert the link, and the renderer respects it.
  const explicitMembers = new Map<string, SetForSupersetDetect[]>();
  const explicitIds = new Set<string>();
  for (const s of sorted) {
    const id = extractSupersetId(s.tags);
    if (id !== null) {
      explicitIds.add(s.id);
      const arr = explicitMembers.get(id) ?? [];
      arr.push(s);
      explicitMembers.set(id, arr);
    }
  }

  const groups: SupersetGroup[] = [];
  for (const [id, members] of explicitMembers) {
    if (members.length < 2) continue;
    const slugs = distinctInOrder(members.map((s) => s.exerciseSlug));
    if (slugs.length < 2) continue;
    groups.push({
      id: `${SUPERSET_TAG_PREFIX}${id}`,
      source: "explicit",
      slugs,
      setIds: members.map((s) => s.id),
    });
  }

  // Pass 2: derived alternation runs over the non-explicit candidates.
  const candidates = sorted.filter((s) => !explicitIds.has(s.id));
  let i = 0;
  while (i < candidates.length) {
    let j = i;
    while (
      j + 1 < candidates.length &&
      candidates[j]!.exerciseSlug !== candidates[j + 1]!.exerciseSlug
    ) {
      j++;
    }
    // Greedy walk above absorbs every "next slug differs" step. For
    // A-B-A-B-C that yields the run A-B-A-B-C (length 5), but the file-
    // header contract says trailing solos belong outside the group:
    // A-B-A-B groups the superset, the lone C falls through. Trim the
    // tail while the last slug only appears once in the run-so-far.
    let run = candidates.slice(i, j + 1);
    while (run.length >= 3) {
      const lastSlug = run[run.length - 1]!.exerciseSlug;
      const occurrences = run.filter(
        (r) => r.exerciseSlug === lastSlug,
      ).length;
      if (occurrences <= 1) run.pop();
      else break;
    }
    if (run.length >= 3) {
      const slugs = distinctInOrder(run.map((s) => s.exerciseSlug));
      if (slugs.length >= 2) {
        groups.push({
          id: `derived-${run[0]!.id}`,
          source: "derived",
          slugs,
          setIds: run.map((s) => s.id),
        });
      }
    }
    // Resume the outer walk past the maximal run we found, even if we
    // trimmed it — the trimmed-tail solos are themselves candidates
    // for ungrouped processing on the next iteration.
    i = j + 1;
  }

  return groups;
}

// Convenience: the single "active" exercise in a session = the slug of
// the latest LogSet by performedAt. UI uses this to light up the Play
// pill on the corresponding catalog row.
export function activeExerciseSlug(
  sets: readonly SetForSupersetDetect[],
): string | null {
  if (sets.length === 0) return null;
  let latest = sets[0]!;
  for (const s of sets) {
    if (s.performedAt > latest.performedAt) latest = s;
  }
  return latest.exerciseSlug;
}
